import React, { useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import Chart from 'chart.js/auto';

const ReceitaCidadeSection = ({ prepararDadosPareto }) => {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    if (!canvasRef.current || !prepararDadosPareto || !prepararDadosPareto.datasets) return;

    const ctx = canvasRef.current.getContext('2d');
    const styles = getComputedStyle(document.documentElement);
    const textMuted = styles.getPropertyValue('--text-muted').trim() || '#6b7280';
    const textPrimary = styles.getPropertyValue('--text-primary').trim() || '#111827';
    const border = styles.getPropertyValue('--border').trim() || 'rgba(0,0,0,0.08)';
    const surface = styles.getPropertyValue('--surface').trim() || '#ffffff';
    
    // Configurações otimizadas para alta qualidade
    const opcoes = {
      responsive: true,
      maintainAspectRatio: false,
      devicePixelRatio: window.devicePixelRatio || 1,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 11,
              weight: '600'
            },
            color: textPrimary
          }
        },
        tooltip: {
          backgroundColor: surface,
          titleColor: textPrimary,
          bodyColor: textPrimary,
          borderColor: border,
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          titleFont: {
            size: 13,
            weight: '600'
          },
          bodyFont: {
            size: 12
          },
          padding: 12,
          callbacks: {
            title: function(context) {
              return context[0].label;
            },
            label: function(context) {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              
              if (label.includes('Receita Acumulada')) {
                return `${label}: ${value.toFixed(1)}%`;
              } else {
                return `${label}: R$ ${(value / 1000000).toFixed(2)}M`;
              }
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 10,
              weight: '500'
            },
            color: textMuted,
            maxRotation: 45,
            minRotation: 45
          },
          title: {
            display: true,
            text: 'Cidades',
            font: {
              size: 11,
              weight: '600'
            },
            color: textPrimary,
            padding: {
              top: 10
            }
          }
        },
        y: {
          type: 'linear',
          position: 'left',
          grid: {
            color: border,
            drawBorder: false
          },
          ticks: {
            font: {
              size: 10,
              weight: '500'
            },
            color: textMuted,
            callback: function(value) {
              if (value >= 1000000) {
                return `R$ ${(value / 1000000).toFixed(1)}M`;
              } else if (value >= 1000) {
                return `R$ ${(value / 1000).toFixed(0)}K`;
              }
              return `R$ ${value.toFixed(0)}`;
            }
          },
          title: {
            display: true,
            text: 'Receita (R$)',
            font: {
              size: 11,
              weight: '600'
            },
            color: textPrimary,
            padding: {
              bottom: 10
            }
          }
        },
        y1: {
          type: 'linear',
          position: 'right',
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 10,
              weight: '500'
            },
            color: textMuted,
            callback: function(value) {
              return `${value.toFixed(0)}%`;
            }
          },
          title: {
            display: true,
            text: 'Receita Acumulada (%)',
            font: {
              size: 11,
              weight: '600'
            },
            color: textPrimary,
            padding: {
              bottom: 10
            }
          }
        }
      },
      interaction: {
        intersect: false,
        mode: 'index'
      },
      elements: {
        point: {
          radius: 4,
          hoverRadius: 6,
          backgroundColor: '#dc2626',
          borderColor: surface,
          borderWidth: 2
        },
        line: {
          tension: 0.4
        }
      }
    };

    // Criar o gráfico com alta qualidade
    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: prepararDadosPareto.labels || [],
        datasets: [
          {
            label: 'Receita por Cidade (R$)',
            data: prepararDadosPareto.datasets?.[0]?.data || [],
            backgroundColor: prepararDadosPareto.datasets?.[0]?.backgroundColor || [],
            borderColor: prepararDadosPareto.datasets?.[0]?.borderColor || [],
            borderWidth: 2,
            yAxisID: 'y',
            order: 2
          },
          {
            label: 'Receita Acumulada (%)',
            data: prepararDadosPareto.datasets?.[1]?.data || [],
            type: 'line',
            borderColor: '#EF4444',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderWidth: 3,
            fill: false,
            tension: 0.3,
            yAxisID: 'y1',
            pointBackgroundColor: '#EF4444',
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 2,
            pointRadius: 5,
            order: 1
          }
        ]
      },
      options: opcoes
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [prepararDadosPareto]);

  if (!prepararDadosPareto || !prepararDadosPareto.datasets) {
    return (
      <div className="overflow-hidden h-full flex flex-col">
        <div className="flex items-center justify-between mb-4 min-w-0">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center min-w-0 flex-1">
            <MapPin className="h-5 w-5 text-emerald-600 mr-2 flex-shrink-0" />
            <span className="truncate">Receita por Cidade</span>
          </h3>
        </div>
        <p className="text-sm text-[color:var(--text-muted)] mb-4">Análise de concentração de receita por cidade (Pareto 80/20)</p>
        <div className="text-center text-[color:var(--text-muted)] py-8 flex-1 flex items-center justify-center">
          Nenhum dado disponível para análise
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 min-w-0">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center min-w-0 flex-1">
          <MapPin className="h-5 w-5 text-emerald-600 mr-2 flex-shrink-0" />
          <span className="truncate">Receita por Cidade</span>
        </h3>
      </div>
      <p className="text-sm text-[color:var(--text-muted)] mb-4">Análise de concentração de receita por cidade (Pareto 80/20)</p>
      
      <div className="flex-1 min-h-0 relative overflow-hidden">
        <canvas 
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%'
          }}
        ></canvas>
      </div>
    </div>
  );
};

export default ReceitaCidadeSection;


